import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { supabase } from '../lib/supabase';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';
import { COMMON_BENEFITS } from '../lib/constants';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';

const EditGigModal = ({ isOpen, onClose, gig, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  // Note: urgent_hiring, no_experience_needed, with_housing don't exist in gigs table
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budget_min: '',
    budget_max: '',
    location: '',
    category_id: '',
    benefits: [],
    image_url: null,
    image_position: 50
  });

  useEffect(() => {
    if (gig) {
      // Convert benefits to array if it's a string (handle legacy data)
      // Use spread to copy array and avoid mutating original data, filter out empty values
      let benefitsArray = [];
      if (Array.isArray(gig.benefits)) {
        benefitsArray = gig.benefits.filter(b => b && typeof b === 'string' && b.trim());
      } else if (typeof gig.benefits === 'string' && gig.benefits) {
        benefitsArray = gig.benefits.split(',').map(b => b.trim()).filter(b => b);
      }

      setFormData({
        title: gig.title || '',
        description: gig.description || '',
        budget_min: gig.budget_min || '',
        budget_max: gig.budget_max || '',
        location: gig.location || '',
        category_id: gig.category_id || '',
        benefits: benefitsArray,
        image_url: gig.image_url || null,
        image_position: gig.image_position ?? 50
      });
    }
  }, [gig]);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error(t('gig.categoryFetchFailed', 'Failed to load categories. Please refresh the page.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t('gig.pleaseLoginEdit', 'Please log in to edit gigs'));
        setLoading(false);
        return;
      }

      // ===== INPUT VALIDATION =====
      // Validate title
      if (!formData.title || formData.title.trim().length < 3) {
        toast.error(t('gig.titleTooShort', 'Gig title must be at least 3 characters'));
        setLoading(false);
        return;
      }

      // Validate description
      if (!formData.description || formData.description.trim().length < 20) {
        toast.error(t('gig.descriptionTooShort', 'Gig description must be at least 20 characters'));
        setLoading(false);
        return;
      }

      // Validate location
      if (!formData.location) {
        toast.error(t('gig.selectLocation', 'Please select a location'));
        setLoading(false);
        return;
      }

      // Validate category
      if (!formData.category_id) {
        toast.error(t('gig.selectCategory', 'Please select a category'));
        setLoading(false);
        return;
      }

      // Validate budget values if provided - use Math.floor for integers
      const budgetMin = formData.budget_min ? Math.floor(Number(formData.budget_min)) : null;
      const budgetMax = formData.budget_max ? Math.floor(Number(formData.budget_max)) : null;
      if (budgetMin !== null && (isNaN(budgetMin) || budgetMin <= 0)) {
        toast.error(t('gig.invalidBudgetMin', 'Please enter a valid minimum budget (must be greater than 0)'));
        setLoading(false);
        return;
      }
      if (budgetMax !== null && (isNaN(budgetMax) || budgetMax <= 0)) {
        toast.error(t('gig.invalidBudgetMax', 'Please enter a valid maximum budget (must be greater than 0)'));
        setLoading(false);
        return;
      }
      if (budgetMin !== null && budgetMax !== null && budgetMin > budgetMax) {
        toast.error(t('gig.budgetRangeError', 'Minimum budget cannot be greater than maximum budget'));
        setLoading(false);
        return;
      }
      // ===== END INPUT VALIDATION =====

      const response = await api.put(`/api/gigs/${gig.id}`, {
        ...formData,
        budget_min: budgetMin,
        budget_max: budgetMax
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.data.success) {
        toast.success(t('gig.updateSuccess', 'Gig updated successfully!'));
        onSuccess(response.data.gig);
        onClose();
      } else {
        toast.error(response.data.detail || t('gig.updateFailed', 'Failed to update gig'));
      }
    } catch (error) {
      console.error('Error updating gig:', error);
      toast.error(error.response?.data?.detail || t('gig.updateFailed', 'Failed to update gig'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('gig.editTitle', 'Edit Gig Post')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('gig.gigTitle', 'Gig Title')} *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Describe what needs to be done, expected timeline, materials needed, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget_min">Minimum Budget (₱) <span className="text-muted-foreground font-normal">(Optional)</span></Label>
              <Input
                id="budget_min"
                type="number"
                value={formData.budget_min}
                onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                onWheel={(e) => e.target.blur()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget_max">Maximum Budget (₱) <span className="text-muted-foreground font-normal">(Optional)</span></Label>
              <Input
                id="budget_max"
                type="number"
                value={formData.budget_max}
                onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                onWheel={(e) => e.target.blur()}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Select
              value={formData.location}
              onValueChange={(value) => setFormData({ ...formData, location: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {BOHOL_MUNICIPALITIES.map((municipality) => (
                  <SelectItem key={municipality} value={municipality}>{municipality}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select
              value={formData.category_id}
              onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Note: Quick Filters (urgent_hiring, no_experience_needed, with_housing) removed - these columns don't exist in gigs table */}

          {/* Benefits Selection */}
          <div className="space-y-3">
            <Label>Benefits Offered</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg bg-muted/30">
              {COMMON_BENEFITS.map((benefit) => (
                <div key={benefit} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-gig-benefit-${benefit}`}
                    checked={formData.benefits.includes(benefit)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({
                          ...formData,
                          benefits: [...formData.benefits, benefit]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          benefits: formData.benefits.filter(b => b !== benefit)
                        });
                      }
                    }}
                  />
                  <label
                    htmlFor={`edit-gig-benefit-${benefit}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {benefit}
                  </label>
                </div>
              ))}
            </div>
            {formData.benefits.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Selected: {formData.benefits.join(', ')}
              </p>
            )}
          </div>

          <ImageUpload
            onImageUpload={(imageUrl) => setFormData(prev => ({ ...prev, image_url: imageUrl }))}
            currentImage={formData.image_url}
            label="Gig Image"
            imagePosition={formData.image_position}
            onPositionChange={(pos) => setFormData(prev => ({ ...prev, image_position: pos }))}
          />

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditGigModal;
